import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock, User, Shield, AlertTriangle, Info } from "lucide-react";
import { isSessionValid } from "@/lib/auth-utils";

export function SessionExpirationTest() {
  const { user, showSessionExpiredModal } = useAuth();
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [checkResult, setCheckResult] = useState<string>("");
  const [isChecking, setIsChecking] = useState(false);

  const handleManualCheck = async () => {
    if (!user) {
      setCheckResult("No user logged in");
      return;
    }

    setIsChecking(true);
    setLastCheck(new Date());

    try {
      const isValid = await isSessionValid();
      setCheckResult(isValid ? "Session is valid" : "Session is expired");
    } catch (error) {
      setCheckResult(`Error checking session: ${error}`);
    } finally {
      setIsChecking(false);
    }
  };

  const simulateSessionExpiration = () => {
    // This would simulate a session expiration for testing
    // In a real scenario, this would be triggered by the backend
    setCheckResult("Session expiration simulation triggered");
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Session Expiration Test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="h-4 w-4" />
                Current User Status
              </h3>
              <div className="space-y-1 text-sm">
                <p>
                  <span className="font-medium">Logged in:</span>{" "}
                  <Badge variant={user ? "default" : "secondary"}>
                    {user ? "Yes" : "No"}
                  </Badge>
                </p>
                {user && (
                  <>
                    <p>
                      <span className="font-medium">Email:</span> {user.email}
                    </p>
                    <p>
                      <span className="font-medium">User ID:</span> {user.id}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Session Status
              </h3>
              <div className="space-y-1 text-sm">
                <p>
                  <span className="font-medium">Modal showing:</span>{" "}
                  <Badge
                    variant={
                      showSessionExpiredModal ? "destructive" : "default"
                    }>
                    {showSessionExpiredModal ? "Showing" : "Hidden"}
                  </Badge>
                </p>
                {lastCheck && (
                  <p>
                    <span className="font-medium">Last check:</span>{" "}
                    {lastCheck.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> Session monitoring is now handled directly
              in the Dashboard component. This test component allows you to
              manually check session validity and monitor the session expired
              modal status.
            </AlertDescription>
          </Alert>

          <Alert>
            <Clock className="h-4 w-4" />
            <AlertDescription>
              The session expiration system in the Dashboard automatically
              monitors user activity and checks session validity every 60
              seconds. When a session expires, users see a modal and are
              redirected to the home page after 5 seconds.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <h3 className="font-semibold">Test Controls</h3>
            <div className="flex gap-2">
              <Button
                onClick={handleManualCheck}
                variant="outline"
                disabled={isChecking || !user}>
                {isChecking ? "Checking..." : "Check Session Validity"}
              </Button>
              <Button onClick={simulateSessionExpiration} variant="outline">
                Simulate Expiration
              </Button>
            </div>
            {checkResult && (
              <p className="text-sm text-muted-foreground">{checkResult}</p>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">
              How Dashboard Session Monitoring Works:
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>
                Session monitoring is active only when Dashboard component is
                mounted
              </li>
              <li>Session is checked every 60 seconds automatically</li>
              <li>
                User activity (mouse, keyboard, scroll, touch) triggers
                additional checks
              </li>
              <li>
                API authentication failures automatically trigger session
                expiration
              </li>
              <li>When session expires, user sees a modal with countdown</li>
              <li>
                User is automatically redirected to home page after 5 seconds
              </li>
              <li>All user data is cleared when session expires</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">Development Features:</h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>
                Session monitoring status indicator in Dashboard (development
                mode only)
              </li>
              <li>Manual session check button in Dashboard</li>
              <li>Comprehensive logging for debugging</li>
              <li>Session check counter for monitoring</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
